import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Cookie preferences dialog (BR-5, PC-4). An accessible modal rendered into document.body with a
 * portal (so an inert ancestor can never disable it): focus moves in on open and back to the
 * trigger on close, Tab is trapped, Escape closes without saving, and both categories are real
 * role="switch" controls with visible labels.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ConsentFocusStyles, cc, consentButtonStyle } from './CookieConsentBanner.js';
import { closeCookiePreferences, setAnalyticsConsent } from './store.js';
import { useCookieConsent } from './useCookieConsent.js';
const FOCUSABLE = 'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';
const scrimStyle = {
    position: 'fixed',
    inset: 0,
    zIndex: `calc(${cc.z} + 1)`,
    background: cc.scrim,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    boxSizing: 'border-box',
};
const panelStyle = {
    width: '100%',
    maxWidth: '480px',
    maxHeight: '100%',
    overflowY: 'auto',
    boxSizing: 'border-box',
    background: cc.bg,
    color: cc.fg,
    border: `1px solid ${cc.border}`,
    borderRadius: '12px',
    padding: '20px',
};
const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    padding: '12px 0',
    borderTop: `1px solid ${cc.border}`,
};
const mutedText = { margin: '4px 0 0', fontSize: '14px', lineHeight: 1.5, color: cc.muted };
function ConsentSwitch({ checked, disabled, onChange, labelledBy, describedBy, testId }) {
    return (_jsx("button", { type: "button", role: "switch", "aria-checked": checked, "aria-labelledby": labelledBy, "aria-describedby": describedBy, disabled: disabled, "data-testid": testId, className: "resida-cc-focus", onClick: () => onChange?.(!checked), style: {
            minWidth: '44px',
            minHeight: '44px',
            padding: 0,
            border: 'none',
            background: 'transparent',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            cursor: disabled ? 'not-allowed' : 'pointer',
        }, children: _jsx("span", { "aria-hidden": "true", style: {
                position: 'relative',
                width: '44px',
                height: '24px',
                borderRadius: '12px',
                background: checked ? cc.primary : cc.muted,
                opacity: disabled ? 0.6 : 1,
            }, children: _jsx("span", { style: {
                    position: 'absolute',
                    top: '2px',
                    left: checked ? '22px' : '2px',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: checked ? cc.primaryFg : cc.bg,
                    transition: 'left 120ms ease-out',
                } }) }) }));
}
function DialogPanel({ consent }) {
    const ids = useId();
    const [analyticsOn, setAnalyticsOn] = useState(consent.effective === 'granted');
    const panelRef = useRef(null);
    // Captured during the first render, before focus moves into the dialog: the trigger.
    const [returnFocusTo] = useState(() => typeof document !== 'undefined' ? document.activeElement : null);
    useEffect(() => {
        panelRef.current?.querySelector(FOCUSABLE)?.focus();
        return () => {
            if (returnFocusTo?.isConnected)
                returnFocusTo.focus();
        };
    }, [returnFocusTo]);
    const onKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.stopPropagation();
            closeCookiePreferences();
            return;
        }
        if (event.key !== 'Tab' || !panelRef.current)
            return;
        const focusables = Array.from(panelRef.current.querySelectorAll(FOCUSABLE));
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (!first || !last)
            return;
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        }
        else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };
    const save = () => {
        setAnalyticsConsent(analyticsOn ? 'granted' : 'denied', 'preferences');
        closeCookiePreferences();
    };
    const showGpcNote = consent.gpc && !consent.stored;
    return (_jsxs("div", { style: scrimStyle, onMouseDown: (event) => {
            if (event.target === event.currentTarget)
                closeCookiePreferences();
        }, children: [_jsx(ConsentFocusStyles, {}), _jsxs("div", { ref: panelRef, role: "dialog", "aria-modal": "true", "aria-labelledby": `${ids}-title`, "aria-describedby": `${ids}-intro`, "data-testid": "cookie-preferences-dialog", style: panelStyle, onKeyDown: onKeyDown, children: [_jsx("h2", { id: `${ids}-title`, style: { margin: 0, fontSize: '18px', fontWeight: 600 }, children: "Cookie preferences" }), _jsx("p", { id: `${ids}-intro`, style: { ...mutedText, margin: '8px 0 12px' }, children: "Choose which cookies Resida may use in this browser. Your choice applies across the Resida website and the tenant and landlord portals." }), showGpcNote && (_jsx("p", { "data-testid": "cookie-pref-gpc-note", style: { margin: '0 0 12px', fontSize: '14px', lineHeight: 1.5, padding: '8px 12px', borderRadius: '8px', border: `1px solid ${cc.border}` }, children: "Your browser is sending a Global Privacy Control signal, so analytics are off. We honor it unless you turn analytics on here." })), _jsxs("div", { style: rowStyle, children: [_jsxs("div", { children: [_jsx("div", { id: `${ids}-necessary`, style: { fontSize: '14px', fontWeight: 600 }, children: "Necessary" }), _jsx("p", { id: `${ids}-necessary-desc`, style: mutedText, children: "Always on. Required for sign-in, security and remembering this choice." })] }), _jsx(ConsentSwitch, { checked: true, disabled: true, labelledBy: `${ids}-necessary`, describedBy: `${ids}-necessary-desc`, testId: "cookie-pref-necessary" })] }), _jsxs("div", { style: rowStyle, children: [_jsxs("div", { children: [_jsx("div", { id: `${ids}-analytics`, style: { fontSize: '14px', fontWeight: 600 }, children: "Analytics" }), _jsx("p", { id: `${ids}-analytics-desc`, style: mutedText, children: "Helps us understand how Resida is used and improve it (Google Analytics and PostHog, including session recordings with typed input masked)." })] }), _jsx(ConsentSwitch, { checked: analyticsOn, onChange: setAnalyticsOn, labelledBy: `${ids}-analytics`, describedBy: `${ids}-analytics-desc`, testId: "cookie-pref-analytics" })] }), _jsxs("div", { style: { display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: '8px', paddingTop: '12px', borderTop: `1px solid ${cc.border}` }, children: [_jsx("button", { type: "button", "data-testid": "cookie-pref-cancel", className: "resida-cc-focus", style: consentButtonStyle('secondary'), onClick: closeCookiePreferences, children: "Cancel" }), _jsx("button", { type: "button", "data-testid": "cookie-pref-save", className: "resida-cc-focus", style: consentButtonStyle('primary'), onClick: save, children: "Save preferences" })] })] })] }));
}
export function CookiePreferencesDialog() {
    const consent = useCookieConsent();
    if (consent.status !== 'resolved' || !consent.preferencesOpen || typeof document === 'undefined')
        return null;
    return createPortal(_jsx(DialogPanel, { consent: consent }), document.body);
}
//# sourceMappingURL=CookiePreferencesDialog.js.map